import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getCurrentUser } from '@/services/auth.service';
import { useTranslation } from 'react-i18next';
import { getUsers } from '@/services/user.service';
import frFR from 'rsuite/locales/fr_FR';

import { ArrowLeftLine, ArrowRightLine, Icon, Trash } from '@rsuite/icons';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  format,
  getWeek,
  isBefore,
  isSameMonth,
  isToday,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { LuCalendarDays } from 'react-icons/lu';
import {
  Button,
  Heading,
  IconButton,
  Panel,
  Stack,
  SelectPicker,
  Modal,
  Form,
  DatePicker,
  TagPicker,
  Input,
  InputGroup,
  Message,
  useToaster,
  Calendar,
  Avatar,
  Loader,
  Whisper,
  Tooltip,
  Card,
  Popover,
  Placeholder,
  Tag
} from 'rsuite';

import ConfirmModal from '@/components/ConfirmModal';
import './schedule.less';
import { TbTemplate, TbUser } from 'react-icons/tb';
import {
  createOrUpdatePlanning,
  getPlanningByWeeks,
  getWfhProfiles
} from '@/services/wfhProfile.service';
import { CiImport, CiExport, CiFileOn } from 'react-icons/ci';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ModalUploadPlanning from './ModalUploadPlanning';
import { FaRegFileAlt } from 'react-icons/fa';
import { getServicelines } from '@/services/serviceline.service';
import calendarImage from '@/images/Calendar.svg';

const ScheduleProfiles = () => {
  const dispatch = useAppDispatch();

  const userConnected = getCurrentUser();
  const toaster = useToaster();

  const profiles = useAppSelector((state: any) => state.wfhProfile?.data)?.map(item => ({
    ...item,
    value: item._id,
    label: item.name
  }));

  const loadingUsers = useAppSelector((state: any) => state.users.loading);

  const users = useAppSelector((state: any) => state.users.data);
  const planning = useAppSelector((state: any) => state.wfhProfile.planning);
  const loadingPlanning = useAppSelector((state: any) => state.wfhProfile.loading);

  const [selectedUsers, setSelectedUsers] = useState([]);

  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(generateWeek(new Date()));

  const [weeks, setWeeks] = useState(generateWeeksOfMonth(new Date()));

  const [view, setView] = useState('monthly');

  const [showModal, setShowModal] = useState<any>({ open: false });

  const [selectedMonth, setSelectedMonth] = useState(new Date());

  const [loadingSave, setLoadingSave] = useState(false);

  const [formValue, setFormValue] = useState<any>({});
  const departments = useAppSelector((state: any) => state.serviceLine.data)?.map(item => {
    return { value: item._id, label: item.serviceLine };
  });
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    title: '',
    subtitle: '',
    loading: false
  });
  const [selectedDays, setSelectedDays] = useState([]);
  const [open, setOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const loadingDepartments = useAppSelector((state: any) => state.serviceLine.loading);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  useEffect(() => {
    dispatch(getUsers());
    dispatch(getWfhProfiles());
  }, []);
  const handleChangeDepartments = value => {
    setSelectedDepartments(value);
  };
  useEffect(() => {
    dispatch(getServicelines());
  }, [dispatch]);
  useEffect(() => {
    if (view == 'monthly') {
      const year = selectedMonth.getFullYear();
      const weekList = Array.from(weeks, ({ weekNumber }) => weekNumber);
      fetchPlanningByMonth(weekList, year);
    } else {
      const { weekNumber, year } = getWeekInfo(selectedWeek[0]);
      fetchPlanningByMonth([weekNumber], year);
    }
  }, [view, selectedMonth, weeks, selectedWeek, users]);

  const renderMenuItem = (label, item) => {
    return (
      <div>
        {label}
        <div
          style={{
            display: 'flex',
            gap: 10
          }}
        >
          {/* {item.authorizedWfhDays?.length > 0 ? (
            <p>
              (
              {item.authorizedWfhDays.map((day, index) => (
                <span key={index}>
                  {day}
                  {index < item.authorizedWfhDays.length - 1 ? ' - ' : ''}
                </span>
              ))}
              )
            </p>
          ) : (
            <p>{t('No authorized days')}</p>
          )} */}

          {item.authorizedWfhDays?.length > 0 ? (
            <div style={{ display: 'flex' }}>
              {item.authorizedWfhDays.map((day, index) => (
                <Tag key={index}>{day}</Tag>
              ))}
            </div>
          ) : (
            <p>{t('No authorized days')}</p>
          )}
        </div>
      </div>
    );
  };

  const handleChangeMembers = selectedTags => {
    if (selectedTags.includes('all')) {
      setSelectedUsers([...Array.from(users, ({ _id }) => _id), 'all']);
    } else {
      setSelectedUsers(selectedTags);
    }
    setSelectedDepartments([]);
    setSelectedDays([]);
  };

  const handleDateSelect = date => {
    const weeksInMonth = generateWeeksOfMonth(date);
    setWeeks(weeksInMonth);
    setSelectedMonth(date);
    setShowCalendar(false);
  };

  function generateWeeksOfMonth(date) {
    const weeks: any = [];
    let currentWeekStart = startOfWeek(startOfMonth(date), { weekStartsOn: 1 });

    const end = endOfMonth(date);

    while (isBefore(currentWeekStart, end) || isSameMonth(currentWeekStart, end)) {
      weeks.push({
        weekNumber: getWeek(currentWeekStart, { weekStartsOn: 1 }),
        startDate: currentWeekStart
      });
      currentWeekStart = addWeeks(currentWeekStart, 1);
    }

    return weeks;
  }

  function getWeekInfo(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNumber = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return {
      weekNumber,
      year: d.getUTCFullYear()
    };
  }
  function isWeekInPast(weekNumber, year) {
    const now = new Date();

    const currentYear = now.getFullYear();
    const currentWeek = getWeekInfo(now).weekNumber;

    if (year < currentYear) return true;
    if (year > currentYear) return false;

    return weekNumber < currentWeek;
  }

  const fetchPlanningByMonth = async (weeks, year) => {
    try {
      await dispatch(getPlanningByWeeks({ userIds: users, weeks: weeks, year }));
    } catch (error) {
      console.error(error);
    }
  };

  const handlePrevMonth = () => {
    const newMonth = subMonths(selectedMonth, 1);
    setSelectedMonth(newMonth);
    const weeksOfMonth = generateWeeksOfMonth(newMonth);
    setWeeks(weeksOfMonth);
    // const year = newMonth.getFullYear();
    // fetchPlanningByMonth(weeksOfMonth, year);
  };

  const handleNextMonth = () => {
    const newMonth = addMonths(selectedMonth, 1);
    setSelectedMonth(newMonth);
    const weeksOfMonth = generateWeeksOfMonth(newMonth);
    setWeeks(weeksOfMonth);
    // const year = newMonth.getFullYear();
    // fetchPlanningByMonth(weeksOfMonth, year);
  };
  const { t } = useTranslation();

  function isCurrentWeek(weekStart) {
    const today = new Date();
    return isWithinInterval(today, {
      start: weekStart,
      end: addDays(weekStart, 6)
    });
  }

  const handleNextWeek = () => {
    const nextMonday = new Date(selectedWeek[selectedWeek.length - 1]);
    nextMonday.setDate(nextMonday.getDate() + 7);
    setSelectedWeek(generateWeek(nextMonday));
  };

  const handlePrevWeek = () => {
    const prevSunday = new Date(selectedWeek[0]);
    prevSunday.setDate(prevSunday.getDate() - 7);
    setSelectedWeek(generateWeek(prevSunday));
  };

  const handleSave = () => {
    try {
      setLoadingSave(true);
      dispatch(createOrUpdatePlanning(formValue))
        .then(() => {
          toaster.push(
            <Message showIcon type="success" closable>
              {t('successfully updated')}
            </Message>
          );
          setShowModal({ open: false });
          setFormValue({});
        })
        .catch(() => {
          toaster.push(
            <Message showIcon type="success" closable>
              {t('Error updating planning')}
            </Message>
          );
        });
      setLoadingSave(false);
    } catch (error) {
      console.error(error);
    }
  };

  const exportPlanningToExcel = (planning, users) => {
    const headers = [
      'Full name',
      'Registration number',
      ...weeks.map(week => `Week ${week.weekNumber}`)
    ];

    const rows = users.map(user => {
      const row = [`${user.firstname} ${user.lastname}`, user.registrationNumber];

      weeks.forEach(({ weekNumber }) => {
        const year = selectedMonth.getFullYear();
        const planningItem = planning.find(
          p => p.user === user._id && p.weekNumber === weekNumber && p.year === year
        );
        row.push(planningItem?.wfhProfile?.name);
      });
      return row;
    });

    const worksheetData = [headers, ...rows];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Planning');

    const fileName = `planning_${format(
      selectedMonth,
      'LLLL'
    )}_${selectedMonth.getFullYear()}.xlsx`;
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, fileName);
  };

  return (
    <div className="resource-scheduler">
      <Panel
        style={{
          padding: 5,
          marginBottom: 5
        }}
      >
        <Form
          style={{
            display: 'flex',
            justifyContent: view == 'monthly' ? 'space-between' : 'flex-start',
            alignItems: view == 'monthly' ? 'center' : '',
            flexDirection: 'row',
            gap: 10
          }}
        >
          <Form.Group>
            <Form.ControlLabel>{t('Members')}</Form.ControlLabel>
            <TagPicker
              data={[
                ...users.map(item => ({
                  value: item._id,
                  label: item.firstname + ' ' + item.lastname
                }))
              ]}
              style={{ width: 225 }}
              placeholder={t('Select members')}
              loading={loadingUsers}
              placement="auto"
              value={selectedUsers}
              onChange={handleChangeMembers}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel style={{ fontSize: 13, fontWeight: 'normal' }}>
              {t('Teams')}
            </Form.ControlLabel>
            <TagPicker
              data={departments}
              style={{ width: 225 }}
              placeholder={t('Select Teams')}
              loading={loadingDepartments}
              placement="auto"
              value={selectedDepartments}
              onChange={handleChangeDepartments}
            />
          </Form.Group>
          {view == 'weekly' && (
            <Form.Group>
              <Form.ControlLabel>{t('Days of week')}</Form.ControlLabel>
              <TagPicker
                data={[
                  { label: t('Monday'), value: 'Monday' },
                  { label: t('Tuesday'), value: 'Tuesday' },
                  { label: t('Wednesday'), value: 'Wednesday' },
                  { label: t('Thursday'), value: 'Thursday' },
                  { label: t('Friday'), value: 'Friday' }
                ]}
                style={{ width: 225 }}
                placeholder={t('Select days')}
                placement="auto"
                value={selectedDays}
                onChange={value => setSelectedDays(value)}
              />
            </Form.Group>
          )}
          {view == 'monthly' && (
            <Stack
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                flexDirection: 'row',
                gap: 10
              }}
            >
              <Whisper
                placement="top"
                controlId="control-id-hover"
                trigger="hover"
                speaker={<Tooltip>{t('Export the planning')}</Tooltip>}
                onClick={() => exportPlanningToExcel(planning, users)}
              >
                <IconButton icon={<Icon as={CiExport} />}></IconButton>
              </Whisper>
              <Whisper
                placement="top"
                controlId="control-id-hover"
                trigger="hover"
                speaker={
                  <Tooltip>
                    {t('Import Excel file. Please name the file ending with _YYYY')}
                  </Tooltip>
                }
                onClick={() => setOpen(true)}
              >
                <IconButton icon={<Icon as={CiImport} />}></IconButton>
              </Whisper>
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip>{t('Show import template')}</Tooltip>}
                onClick={() => setTemplateDialogOpen(true)}
              >
                <IconButton icon={<Icon as={CiFileOn} />} />
              </Whisper>
            </Stack>
          )}
        </Form>
        <Stack justifyContent="flex-end">
          <SelectPicker
            data={[
              {
                label: t('Monthly view'),
                value: 'monthly'
              },
              {
                label: t('Weekly view'),
                value: 'weekly'
              }
            ]}
            style={{ width: 120, marginTop: 20 }}
            placeholder={t('Select view')}
            placement="auto"
            searchable={false}
            cleanable={false}
            value={view}
            onChange={e => setView(e)}
          />
        </Stack>
        {selectedUsers?.length == 0 &&
        selectedDepartments.length == 0 &&
        selectedDays.length == 0 ? (
          <Stack justifyContent="center" direction="column" style={{ marginTop: 50 }} spacing={20}>
            <p>{t('Select users or teams to see their schedules.')}</p>
            <img src={calendarImage} width="100%" />
          </Stack>
        ) : (
          <div style={{ display: 'flex' }}>
            {view == 'monthly' ? (
              <div
                // className="scheduler "
                style={{
                  width: '100%'
                }}
              >
                <div
                  className="scheduler"
                  style={{
                    display: 'grid',
                    height: '50px',
                    gridTemplateColumns: `120px repeat(${weeks.length + 1}, 1fr)`,
                    gap: '2px',
                    background: 'transparent',
                    padding: '2px',
                    borderRadius: '8px',
                    marginTop: '10px',
                    justifyItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <Button onClick={() => setShowCalendar(true)} appearance="subtle">
                      <LuCalendarDays />
                    </Button>
                    <Heading level={5}> {t(format(selectedMonth, 'MMMM'))}</Heading>
                    <IconButton
                      appearance="subtle"
                      circle
                      icon={<ArrowLeftLine />}
                      onClick={handlePrevMonth}
                    />
                  </div>
                  {weeks.map(({ weekNumber, startDate }, index) => (
                    <div
                      style={{
                        textAlign: 'center',
                        height: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                      key={index}
                    >
                      <strong
                        style={{
                          fontSize: 12,
                          color: isCurrentWeek(startDate)
                            ? 'var(--rs-primary-500)'
                            : 'var(--rs-text-primary)',
                          lineHeight: '1.2'
                        }}
                      >
                        {t('W')} {weekNumber}
                      </strong>
                      <span
                        style={{
                          fontSize: 12,
                          color: isCurrentWeek(startDate)
                            ? 'var(--rs-primary-500)'
                            : 'var(--rs-text-primary)',
                          marginTop: 4
                        }}
                      >
                        {t(format(startDate, 'dd'))}.{t(format(startDate, 'MM'))}
                      </span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      appearance="subtle"
                      circle
                      icon={<ArrowRightLine />}
                      onClick={handleNextMonth}
                    />
                  </div>
                </div>
                {loadingPlanning ? (
                  <div
                    style={{
                      marginTop: 50,
                      textAlign: 'center'
                    }}
                  >
                    <Loader />
                  </div>
                ) : (
                  <div style={{ marginTop: 30, height: '400px', overflowY: 'auto' }}>
                    {users
                      ?.filter(
                        resource =>
                          (selectedUsers.length === 0 || selectedUsers.includes(resource._id)) &&
                          (selectedDepartments.length === 0 ||
                            selectedDepartments.includes(resource.serviceLine?._id))
                      )

                      ?.map((resource, index) => (
                        <div
                          key={index}
                          className="scheduler"
                          style={{
                            position: 'relative',
                            display: 'grid',
                            // height: '100px',
                            gridTemplateColumns: `120px repeat(${weeks.length}, 1fr)`,
                            gap: '2px',
                            background: 'rgb(240 244 248)',
                            padding: '5px',
                            borderRadius: '8px',
                            marginTop: '10px',
                            width: '90%',
                            minHeight: '70px'
                          }}
                        >
                          <div className="room-name">
                            <Stack alignItems="center" spacing={5}>
                              {resource.photo ? (
                                <Avatar
                                  circle
                                  src={process.env.REACT_APP_IMAGE_URL + resource.photo}
                                  alt={resource.firstname + resource.lastname}
                                  size="sm"
                                />
                              ) : (
                                <Avatar circle size="sm" />
                              )}
                              <strong
                                style={{
                                  fontSize: 12,
                                  wordBreak: 'break-word',
                                  whiteSpace: 'normal',
                                  textAlign: 'center',
                                  maxWidth: 60
                                }}
                              >
                                {resource.firstname} {resource.lastname}
                              </strong>
                            </Stack>
                          </div>

                          {weeks.map((week, index) => {
                            // const today = startOfDay(new Date());
                            // const targetDate = startOfDay(new Date(week.));
                            // const isPast = isBefore(targetDate, today);
                            const year = selectedMonth.getFullYear();
                            const weekPlanning = planning?.find(
                              item =>
                                item.user == resource._id &&
                                item.weekNumber == week.weekNumber &&
                                year == item.year
                            );

                            const authorizedDays = weekPlanning
                              ? weekPlanning?.wfhProfile?.authorizedWfhDays
                              : resource.wfhProfile?.authorizedWfhDays;
                            const isPast = isWeekInPast(week.weekNumber, year);
                            return (
                              <div
                                key={index}
                                // className={`${time.includes('30') ? 'half-cell' : 'cell'}`}
                                onClick={() => {
                                  if (isPast) return;
                                  setShowModal({
                                    open: true
                                  });
                                  setFormValue({
                                    weekNumber: week.weekNumber,
                                    userId: resource._id,
                                    year: year,
                                    profileId: weekPlanning?.wfhProfile?._id
                                  });
                                }}
                                className={`cell ${isPast ? 'disabled' : ''} 
                        `}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {authorizedDays?.map(day => (
                                    <span key={day} style={{ fontSize: 10, fontWeight: 12 }}>
                                      {t(day)}
                                    </span>
                                  ))}
                                  {/* {resource.wfhProfile?.authorizedWfhDays.map(day => (
                                // <Tag key={day}>{day}</Tag>
                                // <p key={day}>{day}</p>

                                <span key={day} style={{ fontSize: 10, fontWeight: 12 }}>
                                  {day}
                                </span>
                              ))} */}
                                </div>
                                {/* <div className=""> {renderSlot(resource, date, 'AM')} </div>

                          <div
                            style={{
                              borderTop: '1px solid var(--rs-border-primary)',
                              marginTop: 5,
                              marginBottom: 5
                            }}
                          />

                          <div className=""> {renderSlot(resource, date, 'PM')} </div> */}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ) : (
              <div
                // className="scheduler "
                style={{
                  width: '100%'
                }}
              >
                <div
                  className="scheduler"
                  style={{
                    display: 'grid',
                    height: '50px',
                    gridTemplateColumns: `120px repeat(${selectedWeek.length + 1}, 1fr)`,
                    gap: '2px',
                    background: 'transparent',
                    padding: '2px',
                    borderRadius: '8px',
                    marginTop: '10px',
                    justifyItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <Button onClick={() => setShowCalendar(true)} appearance="subtle">
                      <LuCalendarDays />
                    </Button>
                    <Heading level={5}> {t(format(selectedWeek[0], 'MMMM'))}</Heading>
                    <IconButton
                      appearance="subtle"
                      circle
                      icon={<ArrowLeftLine />}
                      onClick={handlePrevWeek}
                    />
                  </div>
                  {selectedWeek.map((date, index) => (
                    <div
                      style={{
                        textAlign: 'center',
                        height: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                      key={index}
                    >
                      <strong
                        style={{
                          fontSize: 12,
                          color: isToday(date) ? 'var(--rs-primary-500)' : 'var(--rs-text-primary)',
                          lineHeight: '1.2'
                        }}
                      >
                        {t(format(date, 'EEE'))}
                      </strong>
                      <span
                        style={{
                          fontSize: 12,
                          color: isToday(date) ? 'var(--rs-primary-500)' : 'var(--rs-text-primary)',
                          marginTop: 4
                        }}
                      >
                        {t(format(date, 'dd'))}.{t(format(date, 'MM'))}
                      </span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      appearance="subtle"
                      circle
                      icon={<ArrowRightLine />}
                      onClick={handleNextWeek}
                    />
                  </div>
                </div>
                {loadingPlanning ? (
                  <div
                    style={{
                      marginTop: 50,
                      textAlign: 'center'
                    }}
                  >
                    <Loader />
                  </div>
                ) : (
                  <div style={{ marginTop: 30, height: '400px', overflowY: 'auto' }}>
                    {users
                      ?.filter(resource => {
                        const { weekNumber, year } = getWeekInfo(selectedWeek[0]);

                        const weekPlanning = planning?.find(
                          item =>
                            item.user === resource._id &&
                            item.weekNumber === weekNumber &&
                            item.year === year
                        );

                        const authorizedDays = weekPlanning
                          ? weekPlanning?.wfhProfile?.authorizedWfhDays
                          : resource.wfhProfile?.authorizedWfhDays;

                        const userFilter =
                          selectedUsers.length === 0 || selectedUsers.includes(resource._id);
                        const daysFilter =
                          !selectedDays ||
                          selectedDays.length === 0 ||
                          authorizedDays?.some(day => selectedDays.includes(day));
                        const departmentFilter =
                          selectedDepartments.length === 0 ||
                          selectedDepartments.includes(resource.serviceLine?._id);

                        return userFilter && daysFilter && departmentFilter;
                      })
                      ?.map((resource, indexUser) => (
                        <div
                          key={indexUser}
                          className="scheduler week-row"
                          style={{
                            position: 'relative',
                            display: 'grid',
                            // height: '100px',
                            gridTemplateColumns: `120px repeat(${selectedWeek.length}, 1fr)`,
                            gap: '2px',
                            background: '#ffffff',
                            padding: '5px',
                            borderRadius: '8px',
                            marginTop: '10px',
                            width: '90%',
                            minHeight: '70px'
                          }}
                        >
                          <div className="room-name">
                            <Stack alignItems="center" spacing={5}>
                              {resource.photo ? (
                                <Avatar
                                  circle
                                  src={process.env.REACT_APP_IMAGE_URL + resource.photo}
                                  alt={resource.firstname + resource.lastname}
                                  size="sm"
                                />
                              ) : (
                                <Avatar circle size="sm" />
                              )}
                              <strong
                                style={{
                                  fontSize: 12,
                                  wordBreak: 'break-word',
                                  whiteSpace: 'normal',
                                  textAlign: 'center',
                                  maxWidth: 60
                                }}
                              >
                                {resource.firstname} {resource.lastname}
                              </strong>
                            </Stack>
                          </div>

                          {selectedWeek.map((date, index) => {
                            const dayName = format(date, 'EEEE');
                            const { weekNumber, year } = getWeekInfo(selectedWeek[0]);

                            const weekPlanning = planning?.find(
                              item =>
                                item.user == resource._id &&
                                item.weekNumber == weekNumber &&
                                year == item.year
                            );
                            const authorizedDays = weekPlanning
                              ? weekPlanning?.wfhProfile?.authorizedWfhDays
                              : resource.wfhProfile?.authorizedWfhDays;
                            const isAuthorized = authorizedDays?.includes(dayName);
                            const isPast = isWeekInPast(weekNumber, year);

                            return (
                              <div
                                key={index}
                                // className={`${time.includes('30') ? 'half-cell' : 'cell'}`}
                                onClick={() => {
                                  if (isPast) return;
                                  setShowModal({
                                    open: true
                                  });
                                  setFormValue({
                                    weekNumber: weekNumber,
                                    userId: resource._id,
                                    year: year,
                                    profileId: weekPlanning?.wfhProfile?._id
                                  });
                                }}
                                className={`cell-week ${isPast ? 'disabled' : ''} 
                        `}
                              >
                                {isAuthorized && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      fontSize: 18,
                                      color: '#4CAF50'
                                    }}
                                  >
                                    ✔
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Panel>

      {showModal.open && (
        <Modal
          open={showModal.open}
          onClose={() => {
            setShowModal({ open: false });
            setFormValue({});
          }}
          size="sm"
        >
          <Modal.Header>
            <Modal.Title>
              <strong>Update Home Office Profile</strong>
              <br />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginTop: 30 }}>
              <Form fluid formValue={formValue} onChange={value => setFormValue(value)}>
                <Stack justifyContent="space-between" wrap>
                  <Form.Group>
                    <Form.ControlLabel>{t('Home Office Profile')}</Form.ControlLabel>
                    <SelectPicker
                      data={profiles}
                      value={formValue.profileId}
                      renderMenuItem={(label, item) => renderMenuItem(label, item)}
                      placement="auto"
                      style={{ width: 223 }}
                      onChange={e => setFormValue(prev => ({ ...prev, profileId: e }))}
                    />
                  </Form.Group>
                </Stack>
              </Form>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              onClick={() => {
                handleSave();
              }}
              appearance="primary"
              disabled={loadingSave}
              loading={loadingSave}
            >
              {loadingSave ? t('Saving...') : t('Save')}
            </Button>
            <Button
              onClick={() => {
                setShowModal({ open: false });
                setFormValue({});
              }}
              appearance="subtle"
            >
              {t('Cancel')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <Modal open={showCalendar} onClose={() => setShowCalendar(false)}>
        <Modal.Header>
          <Modal.Title>{t('Choose a Week')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Calendar
            // bordered
            onSelect={handleDateSelect}
            compact
            weekStart={1}
            showWeekNumbers={true}
            locale={frFR}
          />
        </Modal.Body>
      </Modal>

      {confirmDialog?.open == true && (
        <ConfirmModal confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
      )}

      {open && (
        <ModalUploadPlanning
          open={open}
          setOpen={setOpen}
          weeks={weeks}
          year={selectedMonth.getFullYear()}
        />
      )}

      <Modal open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} size="sm">
        <Modal.Header>{t('Import Template')}</Modal.Header>
        <Modal.Body>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ccc', padding: '4px' }}>Full name</th>
                <th style={{ border: '1px solid #ccc', padding: '4px' }}>Registration number</th>
                <th style={{ border: '1px solid #ccc', padding: '4px' }}>Week 27</th>
                <th style={{ border: '1px solid #ccc', padding: '4px' }}>Week 28</th>
                <th style={{ border: '1px solid #ccc', padding: '4px' }}>Week 29</th>
                <th style={{ border: '1px solid #ccc', padding: '4px' }}>Week 30</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>User User</td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>12345</td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>Profile 1</td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>Profile 2</td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>Profile 1</td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>Profile 2</td>
              </tr>
            </tbody>
          </table>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ScheduleProfiles;

const generateWeek = date => {
  const start = new Date(date);

  const dayOfWeek = start.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  start.setDate(date.getDate() - daysToSubtract);

  return Array.from({ length: 5 }, (_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    return day;
  });
};
